import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Layout, List, Input, Button, Avatar, Spin, Empty, Tooltip, message as antdMessage, Modal, Select, Form, Popconfirm } from 'antd';
import { SettingOutlined, SendOutlined, UsergroupAddOutlined, ArrowLeftOutlined, PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import axios from 'axios';
import io from 'socket.io-client';
import ChatSettingsModal from './ChatSettingsModal'; // Import the modal
import { useAuth } from '../context/AuthContext'; // Import auth context for user info

const { Header, Content, Footer } = Layout;
// IMPORTANT: For production, REACT_APP_SOCKET_URL should be set to your backend's URL (e.g., https://care360-backend.onrender.com)
// Defaulting to a common production-like URL if REACT_APP_SOCKET_URL is not set.
// This is a temporary measure for testing if the env var is missing.
const SOCKET_URL = process.env.REACT_APP_SOCKET_URL || (window.location.hostname === 'localhost' ? 'http://localhost:3001' : 'https://care360-backend.onrender.com');


const Chatbox = ({ patientId, userId }) => {
  const { user } = useAuth(); // Get current user info for authorization
  const socketRef = useRef(null);
  const [conversations, setConversations] = useState([]);
  const [activeConversation, setActiveConversation] = useState(null);
  const messagesEndRef = useRef(null); // For scrolling to bottom
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loadingConversations, setLoadingConversations] = useState(false);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [settingsModalVisible, setSettingsModalVisible] = useState(false);
  const [messagePage, setMessagePage] = useState(1);
  const [hasMoreMessages, setHasMoreMessages] = useState(true);
  const [newConversationModalVisible, setNewConversationModalVisible] = useState(false);
  const [userSearchOptions, setUserSearchOptions] = useState([]);
  const [isSearchingUsers, setIsSearchingUsers] = useState(false);
  const [form] = Form.useForm();
  const [patientInfo, setPatientInfo] = useState(null);
  const [loadingPatientInfo, setLoadingPatientInfo] = useState(false);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  // Helper function to get patient display name
  const getPatientDisplayName = (patient) => {
    if (!patient) return null;

    // Try different possible field combinations
    // Display as "Last name Middle name First name" format (matching PatientBanner format)
    if (patient.firstName && patient.lastName) {
      const lastName = patient.lastName;
      const middleName = patient.middleName ? ` ${patient.middleName}` : '';
      const firstName = patient.firstName;
      return `${lastName}${middleName} ${firstName}`.trim();
    }
    if (patient.name) {
      return patient.name;
    }
    if (patient.fullName) {
      return patient.fullName;
    }
    if (patient.lastName) {
      return patient.lastName;
    }
    if (patient.firstName) {
      return patient.firstName;
    }

    return null;
  };

  // Check if current user can delete a conversation
  const canDeleteConversation = (conversation) => {
    if (!user || !conversation) {
      console.log('canDeleteConversation: Missing user or conversation', { user: !!user, conversation: !!conversation });
      return false;
    }

    console.log('canDeleteConversation: Checking permissions', {
      userRole: user.role,
      userId: user._id,
      conversationId: conversation._id,
      createdBy: conversation.createdBy,
      participants: conversation.participants
    });

    // Admin can delete any conversation
    if (user.role === 'Admin') {
      console.log('canDeleteConversation: User is Admin - allowing delete');
      return true;
    }

    // Check if user is the creator of the conversation
    // The conversation creator is typically the first participant or stored in createdBy field
    if (conversation.createdBy === user._id || conversation.createdBy === userId) {
      console.log('canDeleteConversation: User is creator - allowing delete');
      return true;
    }

    // Alternative: Check if user is in the initial participants (first participant is usually creator)
    if (conversation.participants && conversation.participants.length > 0) {
      const firstParticipant = conversation.participants[0];
      if (typeof firstParticipant === 'string') {
        // If participants are just IDs
        const isCreator = firstParticipant === user._id || firstParticipant === userId;
        console.log('canDeleteConversation: Checking first participant (string)', { firstParticipant, isCreator });
        return isCreator;
      } else if (firstParticipant._id) {
        // If participants are populated objects
        const isCreator = firstParticipant._id === user._id || firstParticipant._id === userId;
        console.log('canDeleteConversation: Checking first participant (object)', { firstParticipantId: firstParticipant._id, isCreator });
        return isCreator;
      }
    }

    console.log('canDeleteConversation: No permission found - denying delete');
    return false;
  };

  useEffect(scrollToBottom, [messages]);

  // Fetch Patient Information
  const fetchPatientInfo = useCallback(async () => {
    if (!patientId) {
      setPatientInfo(null);
      return;
    }

    setLoadingPatientInfo(true);
    try {
      const token = localStorage.getItem('authToken');
      const response = await axios.get(`/patients/${patientId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const responseData = response.data.data || response.data;
      // Handle nested patient structure
      const patient = responseData.patient || responseData;
      setPatientInfo(patient);
      console.log('Fetched patient info:', responseData);
      console.log('Extracted patient:', patient);
      console.log('Patient firstName:', patient?.firstName);
      console.log('Patient lastName:', patient?.lastName);
      console.log('Patient name fields:', {
        firstName: patient?.firstName,
        lastName: patient?.lastName,
        name: patient?.name,
        fullName: patient?.fullName
      });
    } catch (error) {
      console.error('Error fetching patient info:', error);
      setPatientInfo(null);
    } finally {
      setLoadingPatientInfo(false);
    }
  }, [patientId]);

  // Fetch Conversations
  const fetchConversations = useCallback(async () => {
    if (!patientId) {
      console.log('Chatbox: No patientId provided, clearing conversations');
      setConversations([]);
      return;
    }

    console.log('Chatbox: Fetching conversations for patient:', patientId);
    setLoadingConversations(true);

    try {
      const token = localStorage.getItem('authToken'); // Use correct token key from AuthContext
      console.log('Chatbox: Using token:', token ? 'Token found' : 'No token');

      // Try the patient-specific endpoint first
      const endpoint = `/patients/${patientId}/chat/conversations`;
      console.log('Chatbox: Calling API endpoint:', endpoint);

      const response = await axios.get(endpoint, {
        headers: { Authorization: `Bearer ${token}` }
      });

      console.log('Chatbox: API response:', response.data);
      const conversations = response.data.data || response.data || [];
      console.log('Chatbox: Parsed conversations:', conversations);

      setConversations(conversations);
    } catch (error) {
      console.error("Chatbox: Error fetching conversations:", error);
      console.error("Chatbox: Error response:", error.response?.data);
      console.error("Chatbox: Error status:", error.response?.status);

      // Try alternative endpoint if the first one fails
      if (error.response?.status === 404) {
        console.log('Chatbox: Patient endpoint not found, trying general chat endpoint');
        try {
          const response = await axios.get('/chat/conversations', {
            headers: { Authorization: `Bearer ${localStorage.getItem('authToken')}` },
            params: { patientId: patientId }
          });
          console.log('Chatbox: Alternative API response:', response.data);
          const conversations = response.data.data || response.data || [];
          setConversations(conversations);
          return;
        } catch (altError) {
          console.error("Chatbox: Alternative endpoint also failed:", altError);
        }
      }

      antdMessage.error(`Failed to load conversations: ${error.response?.data?.message || error.message}`);
      setConversations([]);
    } finally {
      setLoadingConversations(false);
    }
  }, [patientId]);

  // Fetch Messages for Active Conversation
  const fetchMessages = useCallback(async (conversationId, page = 1, loadMore = false) => {
    if (!conversationId) return;
    setLoadingMessages(true);
    try {
      const token = localStorage.getItem('authToken'); // Use correct token key from AuthContext
      // Removing leading /api/v1
      const response = await axios.get(`/chat/conversations/${conversationId}/messages?page=${page}&limit=20`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const newMessages = response.data.data || [];
      setMessages(prev => loadMore ? [...newMessages, ...prev] : newMessages); // Prepend if loading more
      setHasMoreMessages(newMessages.length === 20); // Assuming limit is 20
      if (!loadMore) setMessagePage(1); // Reset page if it's a fresh load

      // Mark messages as read
      if (socketRef.current && socketRef.current.connected) {
        socketRef.current.emit('markMessagesAsRead', { conversationId });
      } else {
        // Fallback REST call if socket not ready
        // Removing leading /api/v1
        const tokenForRest = localStorage.getItem('authToken'); // Use correct token key from AuthContext
        axios.post(`/chat/conversations/${conversationId}/messages/mark-read`, {}, {
            headers: { Authorization: `Bearer ${tokenForRest}` }
        }).catch(err => console.error("Error marking messages as read via REST:", err));
      }

    } catch (error) {
      console.error("Error fetching messages:", error);
      antdMessage.error('Failed to load messages.');
    } finally {
      setLoadingMessages(false);
    }
  }, []);


  // Effect for patientId changes (fetch patient info and conversations)
  useEffect(() => {
    if (patientId) {
      fetchPatientInfo();
      fetchConversations();
    } else {
      setPatientInfo(null);
      setConversations([]);
      setActiveConversation(null);
      setMessages([]);
    }
  }, [patientId, fetchPatientInfo, fetchConversations]);

  // Effect for activeConversation changes (fetch its messages)
  useEffect(() => {
    if (activeConversation?._id) {
      setMessages([]); // Clear previous messages
      setHasMoreMessages(true); // Reset for new conversation
      fetchMessages(activeConversation._id, 1);
    } else {
      setMessages([]);
    }
  }, [activeConversation, fetchMessages]);


  // Socket.IO Setup and Event Handlers
  useEffect(() => {
    if (!userId) return; // Don't connect if no user

    const token = localStorage.getItem('authToken'); // Use correct token key from AuthContext
    if (!token) {
        console.error("Chatbox: No auth token found for socket connection.");
        return;
    }

    socketRef.current = io(SOCKET_URL, {
      auth: { token } // Send token for authentication
    });

    socketRef.current.on('connect', () => {
      console.log('Chatbox: Connected to Socket.IO server', socketRef.current.id);

      // Join rooms for existing conversations after fetching them
      conversations.forEach(conv => {
        if (conv._id) socketRef.current.emit('joinConversation', { conversationId: conv._id });
      });
    });

    socketRef.current.on('disconnect', (reason) => {
      console.log('Chatbox: Disconnected from Socket.IO server:', reason);
    });

    socketRef.current.on('connect_error', (err) => {
        console.error('Chatbox: Socket connection error:', err.message, err.data);
        antdMessage.error(`Chat connection error: ${err.message}. Please refresh.`);
    });
    
    socketRef.current.on('receiveChatMessage', (message) => {
      console.log('Received message:', message);
      if (message.conversationId === activeConversation?._id) {
        setMessages(prevMessages => [...prevMessages, message]);
        // Mark as read if this conversation is active
        if (socketRef.current && socketRef.current.connected) {
            socketRef.current.emit('markMessagesAsRead', { conversationId: message.conversationId });
        }
      }
      // Update conversation list (e.g., last message, unread count)
      setConversations(prevConvs => prevConvs.map(conv => 
        conv._id === message.conversationId 
          ? { ...conv, lastMessage: message.content, lastMessageAt: message.timestamp, unreadCount: conv._id === activeConversation?._id ? 0 : (conv.unreadCount || 0) + 1 } 
          : conv
      ).sort((a, b) => new Date(b.lastMessageAt) - new Date(a.lastMessageAt)));
    });

    socketRef.current.on('conversationUpdated', (updatedConv) => {
        console.log('Conversation updated:', updatedConv);
        setConversations(prevConvs => prevConvs.map(c => c._id === updatedConv.conversationId ? {...c, ...updatedConv, lastMessage: updatedConv.lastMessage?.content || c.lastMessage, lastMessageAt: updatedConv.lastMessage?.timestamp || c.lastMessageAt } : c)
            .sort((a, b) => new Date(b.lastMessageAt) - new Date(a.lastMessageAt))
        );
        if (activeConversation?._id === updatedConv.conversationId) {
            setActiveConversation(prev => ({...prev, ...updatedConv}));
        }
    });
    
    socketRef.current.on('newConversation', (newConv) => {
        console.log('New conversation:', newConv);
        setConversations(prevConvs => {
          // Check if conversation already exists to prevent duplicates
          const existingConv = prevConvs.find(conv => conv._id === newConv._id);
          if (existingConv) {
            console.log('Conversation already exists, not adding duplicate:', newConv._id);
            return prevConvs;
          }
          return [newConv, ...prevConvs].sort((a, b) => new Date(b.lastMessageAt) - new Date(a.lastMessageAt));
        });
        if (newConv.participants.find(p => p._id === userId)) { // If current user is part of new conv
            socketRef.current.emit('joinConversation', { conversationId: newConv._id });
        }
    });

    socketRef.current.on('messagesReadUpdate', ({ conversationId: updatedConvId, readerId }) => {
        if (readerId !== userId && activeConversation?._id === updatedConvId) {
            // Another user read messages in the current chat, update UI if displaying read receipts
            console.log(`User ${readerId} read messages in ${updatedConvId}`);
        }
        // Update unread counts if necessary
        setConversations(prevConvs => prevConvs.map(conv => 
            conv._id === updatedConvId ? { ...conv, unreadCount: 0 } : conv // Simplistic: reset if current user read
        ));
    });

    // TODO: Add listeners for 'userTyping', 'addedToConversation', 'removedFromConversation'

    return () => {
      if (socketRef.current) {
        console.log('Chatbox: Disconnecting socket...');
        socketRef.current.disconnect();
        socketRef.current = null;
      }
    };
  }, [userId, conversations, activeConversation?._id]); // Rerun if userId changes, or conversations list changes (to join new ones)


  const handleSelectConversation = (conversation) => {
    if (activeConversation?._id === conversation._id) return;
    setActiveConversation(conversation);
    // Fetching messages and marking as read is handled by useEffect for activeConversation
  };

  const handleSendMessage = () => {
    if (!newMessage.trim() || !activeConversation?._id || !socketRef.current || !socketRef.current.connected) {
        antdMessage.warn('Cannot send message. Check connection or select a conversation.');
        return;
    }
    socketRef.current.emit('sendChatMessage', {
      conversationId: activeConversation._id,
      content: newMessage.trim(),
    });
    // Optimistic update (optional, server will send back the message)
    // setMessages(prev => [...prev, { _id: Date.now().toString(), senderId: { _id: userId, name: 'You' }, content: newMessage.trim(), timestamp: new Date() }]);
    setNewMessage('');
  };
  
  const handleLoadMoreMessages = () => {
    if (activeConversation?._id && hasMoreMessages && !loadingMessages) {
        const nextPage = messagePage + 1;
        fetchMessages(activeConversation._id, nextPage, true);
        setMessagePage(nextPage);
    }
  };

  // Search for users to add to conversation
  const handleUserSearch = useCallback(async (searchText) => {
    console.log('Searching for users with text:', searchText);
    if (!searchText || searchText.length < 2) {
      setUserSearchOptions([]);
      return;
    }
    setIsSearchingUsers(true);
    try {
      const token = localStorage.getItem('authToken');
      console.log('User search - using token:', token ? 'Token found' : 'No token');

      const endpoint = `/users?search=${searchText}&limit=10`;
      console.log('User search - calling endpoint:', endpoint);

      const response = await axios.get(endpoint, {
        headers: { Authorization: `Bearer ${token}` },
      });

      console.log('User search response:', response.data);
      const users = response.data.data.users || response.data.data || [];
      console.log('Found users:', users);

      const options = users
        .filter(user => user._id !== userId) // Exclude current user
        .map(user => ({
          value: user._id,
          label: `${user.name} (${user.email || user.role || 'User'})`,
          user: user
        }));
      console.log('User search options:', options);
      setUserSearchOptions(options);
    } catch (error) {
      console.error('Error searching users:', error);
      console.error('User search error response:', error.response?.data);
      console.error('User search error status:', error.response?.status);
      antdMessage.error('Failed to search users.');
      setUserSearchOptions([]);
    } finally {
      setIsSearchingUsers(false);
    }
  }, [userId]);

  // Create new conversation
  const handleCreateConversation = async (values) => {
    console.log('Creating conversation with values:', values);
    console.log('Current patientId:', patientId);
    console.log('Current userId:', userId);

    try {
      const token = localStorage.getItem('authToken');
      const { participants, name } = values;

      // Include current user in participants
      const allParticipants = [userId, ...participants];
      console.log('All participants:', allParticipants);

      // Backend expects 'initialParticipantIds' field (confirmed from backend code)
      const requestData = {
        name: name || undefined,
        initialParticipantIds: allParticipants, // This is the correct field name
        patientId: patientId
      };
      console.log('Request data:', requestData);
      console.log('Request data JSON:', JSON.stringify(requestData, null, 2));
      console.log('initialParticipantIds type:', typeof requestData.initialParticipantIds);
      console.log('initialParticipantIds isArray:', Array.isArray(requestData.initialParticipantIds));
      console.log('Using token:', token ? 'Token found' : 'No token');

      const response = await axios.post('/chat/conversations', requestData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      console.log('Create conversation response:', response.data);
      const newConversation = response.data.data;
      console.log('Created new conversation:', newConversation);

      // Add to conversations list (check for duplicates)
      setConversations(prev => {
        const existingConv = prev.find(conv => conv._id === newConversation._id);
        if (existingConv) {
          console.log('Conversation already exists in list, not adding duplicate:', newConversation._id);
          return prev;
        }
        return [newConversation, ...prev];
      });

      // Select the new conversation
      setActiveConversation(newConversation);

      // Close modal and reset form
      setNewConversationModalVisible(false);
      form.resetFields();
      setUserSearchOptions([]);

      antdMessage.success('New conversation created!');
    } catch (error) {
      console.error('Error creating conversation:', error);
      console.error('Error response:', error.response?.data);
      console.error('Error status:', error.response?.status);
      console.error('Error message:', error.message);

      // Try to get more specific error information
      const errorMessage = error.response?.data?.message ||
                          error.response?.data?.error ||
                          error.message ||
                          'Unknown error occurred';

      antdMessage.error(`Failed to create conversation: ${errorMessage}`);
    }
  };

  // Delete conversation
  const handleDeleteConversation = async (conversationId) => {
    console.log('Deleting conversation:', conversationId);

    // Find the conversation to check permissions
    const conversation = conversations.find(conv => conv._id === conversationId);
    if (!conversation) {
      antdMessage.error('Conversation not found');
      return;
    }

    // Check if user has permission to delete
    if (!canDeleteConversation(conversation)) {
      antdMessage.error('You do not have permission to delete this conversation. Only admins or the conversation creator can delete conversations.');
      return;
    }

    try {
      const token = localStorage.getItem('authToken');

      await axios.delete(`/chat/conversations/${conversationId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      console.log('Conversation deleted successfully');

      // Remove from conversations list
      setConversations(prev => prev.filter(conv => conv._id !== conversationId));

      // If the deleted conversation was active, clear it
      if (activeConversation?._id === conversationId) {
        setActiveConversation(null);
        setMessages([]);
      }

      antdMessage.success('Conversation deleted successfully!');
    } catch (error) {
      console.error('Error deleting conversation:', error);
      console.error('Delete error response:', error.response?.data);
      console.error('Delete error status:', error.response?.status);

      // Handle specific error cases
      if (error.response?.status === 404) {
        antdMessage.error('Delete conversation feature is not yet implemented on the server. Please contact your system administrator.');
        console.warn('Backend DELETE endpoint for conversations not implemented. Need to add DELETE route in chatRoutes.js');
      } else {
        const errorMessage = error.response?.data?.message ||
                            error.response?.data?.error ||
                            error.message ||
                            'Unknown error occurred';

        antdMessage.error(`Failed to delete conversation: ${errorMessage}`);
      }
    }
  };


  // Show message when no patient is selected
  if (!patientId) {
    return (
      <Layout style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        <Header style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 10px', backgroundColor: '#fff', borderBottom: '1px solid #f0f0f0', height: '48px' }}>
          <h4 style={{ margin: 0, color: '#666' }}>Chat</h4>
        </Header>
        <Content style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#f5f5f5' }}>
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description="Select a patient to start chatting"
            style={{ color: '#666' }}
          />
        </Content>
      </Layout>
    );
  }

  return (
    <Layout style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Header style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 10px', backgroundColor: '#fff', borderBottom: '1px solid #f0f0f0', height: '48px' }}>
        {activeConversation && (
            <Button icon={<ArrowLeftOutlined />} type="text" onClick={() => setActiveConversation(null)} style={{marginRight: '8px'}} />
        )}
        <h4 style={{ margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flexGrow: 1 }}>
          {activeConversation ? (activeConversation.name || (activeConversation.participants?.filter(p=>p._id !== userId).map(p=>p.name).join(', ') || 'Chat')) : 'Conversations'}
        </h4>
        <div>
          {activeConversation && (
            <Tooltip title="Manage Participants">
              <Button icon={<UsergroupAddOutlined />} type="text" onClick={() => setSettingsModalVisible(true)} />
            </Tooltip>
          )}
        </div>
      </Header>

      <Content style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
        {/* Conversation List Sidebar */}
        <div 
            style={{ 
                width: activeConversation ? '0px' : '100%', // Full width if no active conv, else hidden
                minWidth: activeConversation ? '0px' : '120px', // Ensure it can be small
                borderRight: activeConversation ? 'none' : '1px solid #f0f0f0', 
                overflowY: 'auto', 
                transition: 'width 0.3s ease-in-out, min-width 0.3s ease-in-out',
                display: 'flex',
                flexDirection: 'column'
            }}
        >
          {/* New Conversation Button - shown when there are conversations */}
          {!loadingConversations && conversations.length > 0 && (
            <div style={{ padding: '10px 12px', borderBottom: '1px solid #f0f0f0' }}>
              <Button
                type="primary"
                size="small"
                icon={<PlusOutlined />}
                onClick={() => setNewConversationModalVisible(true)}
                style={{ width: '100%' }}
              >
                New Conversation
              </Button>
            </div>
          )}

          {loadingConversations ? <Spin style={{padding: '20px', margin: 'auto'}} /> : (
            <List
              dataSource={conversations}
              rowKey={(item) => item._id} // Ensure unique keys to prevent duplicate rendering
              renderItem={item => (
                <List.Item
                  style={{ padding: '10px 12px', borderBottom: '1px solid #f0f0f0', position: 'relative' }}
                  className={activeConversation?._id === item._id ? 'active-conversation' : ''}
                  actions={canDeleteConversation(item) ? [
                    <Popconfirm
                      key="delete"
                      title="Delete Conversation"
                      description="Are you sure you want to delete this conversation? This action cannot be undone."
                      onConfirm={(e) => {
                        e.stopPropagation();
                        handleDeleteConversation(item._id);
                      }}
                      onCancel={(e) => e.stopPropagation()}
                      okText="Delete"
                      cancelText="Cancel"
                      okType="danger"
                    >
                      <Button
                        type="text"
                        icon={<DeleteOutlined />}
                        size="small"
                        danger
                        onClick={(e) => e.stopPropagation()}
                        style={{ color: '#ff4d4f' }}
                        title={user?.role === 'Admin' ? 'Delete conversation (Admin)' : 'Delete conversation (Creator)'}
                      />
                    </Popconfirm>
                  ] : []}
                >
                  <List.Item.Meta
                    avatar={item.participants && item.participants.length > 2 ? <Avatar icon={<UsergroupAddOutlined />} /> : <Avatar>{item.name?.substring(0,1) || 'U'}</Avatar>}
                    title={
                      <div
                        onClick={() => handleSelectConversation(item)}
                        style={{ cursor: 'pointer', fontSize: '0.9em', fontWeight: item.unreadCount ? 'bold' : 'normal' }}
                      >
                        {(() => {
                          // Debug conversation name display
                          console.log('Conversation display name debug:', {
                            conversationId: item._id,
                            name: item.name,
                            participants: item.participants,
                            userId: userId
                          });

                          // Prioritize conversation name if set
                          if (item.name && item.name.trim()) {
                            return item.name;
                          }

                          // Fallback to participant names (excluding current user)
                          if (item.participants && item.participants.length > 0) {
                            const otherParticipants = item.participants.filter(p => p._id !== userId);
                            if (otherParticipants.length > 0) {
                              return otherParticipants.map(p => p.name || 'Unknown User').join(', ');
                            }
                          }

                          // Final fallback
                          return 'Conversation';
                        })()}
                      </div>
                    }
                    description={
                      <div
                        onClick={() => handleSelectConversation(item)}
                        style={{ cursor: 'pointer' }}
                      >
                        <small style={{fontSize: '0.8em', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', display: 'block', color: item.unreadCount ? '#1890ff' : 'grey'}}>
                            {item.lastMessage || 'No messages yet.'}
                        </small>
                      </div>
                    }
                  />
                  {/* {item.unreadCount > 0 && <Badge count={item.unreadCount} size="small" style={{marginLeft: 'auto'}}/>} */}
                </List.Item>
              )}
            />
          )}
          {!loadingConversations && conversations.length === 0 && (
            <div style={{margin: 'auto', padding: '20px', textAlign: 'center'}}>
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={
                  <div>
                    <p>No conversations yet</p>
                    <p style={{fontSize: '12px', color: '#666'}}>
                      {loadingPatientInfo ? (
                        'Loading patient info...'
                      ) : (() => {
                        const patientName = getPatientDisplayName(patientInfo);
                        return patientName ? `Patient: ${patientName}` : `Patient ID: ${patientId}`;
                      })()}
                    </p>
                  </div>
                }
              />
              <Button
                type="primary"
                size="small"
                icon={<PlusOutlined />}
                style={{marginTop: '10px'}}
                onClick={() => setNewConversationModalVisible(true)}
              >
                Start New Chat
              </Button>
            </div>
          )}
        </div>

        {/* Message Area */}
        <div 
            style={{ 
                flex: 1, 
                display: activeConversation ? 'flex' : 'none', // Show only if conversation is active
                flexDirection: 'column', 
                overflow: 'hidden', 
                backgroundColor: '#f5f5f5' 
            }}
        >
          {activeConversation ? (
            <>
              <Content style={{ padding: '10px', overflowY: 'auto', flexGrow: 1 }} id="message-list-content">
                {hasMoreMessages && !loadingMessages && (
                    <div style={{textAlign: 'center', margin: '10px 0'}}>
                        <Button onClick={handleLoadMoreMessages} size="small">Load More</Button>
                    </div>
                )}
                {loadingMessages && messagePage > 1 && <Spin style={{display: 'block', textAlign: 'center', margin: '10px 0'}}/>}

                {messages.map(msg => (
                  <div key={msg._id} style={{ marginBottom: '10px', display: 'flex', justifyContent: msg.senderId?._id === userId ? 'flex-end' : 'flex-start' }}>
                    <div>
                        <div style={{ fontSize: '0.75em', color: '#888', marginBottom: '2px', textAlign: msg.senderId?._id === userId ? 'right' : 'left' }}>
                        {msg.senderId?.name || 'System'} - {new Date(msg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </div>
                        <div style={{
                        display: 'inline-block',
                        padding: '6px 12px',
                        borderRadius: '15px',
                        backgroundColor: msg.isNotification ? '#f0f0f0' : (msg.senderId?._id === userId ? '#1890ff' : '#e4e6eb'),
                        color: msg.isNotification ? '#555' : (msg.senderId?._id === userId ? '#fff' : '#000'),
                        maxWidth: '250px', // Max width for message bubble
                        wordBreak: 'break-word',
                        }}>
                        {msg.content}
                        </div>
                    </div>
                  </div>
                ))}
                <div ref={messagesEndRef} />
                {!loadingMessages && messages.length === 0 && !hasMoreMessages && <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No messages yet." style={{marginTop: '20px'}}/>}
              </Content>
              <Footer style={{ padding: '10px 12px', backgroundColor: '#fff', borderTop: '1px solid #f0f0f0' }}>
                <Input.Search
                  placeholder="Type a message..."
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  enterButton={<Button type="primary" icon={<SendOutlined />} disabled={!newMessage.trim()} />}
                  onSearch={handleSendMessage}
                  disabled={!activeConversation || loadingMessages}
                />
              </Footer>
            </>
          ) : null }
        </div>
      </Content>
      {activeConversation && ( // Only render modal if there's an active conversation
        <ChatSettingsModal
          visible={settingsModalVisible}
          onClose={() => setSettingsModalVisible(false)}
          conversation={activeConversation} // Pass the full activeConversation object
          currentUserId={userId}
          onParticipantsUpdate={(updatedConversationData) => {
            // Update the active conversation in state
            setActiveConversation(prev => ({...prev, ...updatedConversationData}));
            // Optionally, refresh the main conversations list if participant changes affect its display
            fetchConversations(); // Or rely on socket events for this
            antdMessage.success('Participants updated.');
          }}
        />
      )}

      {/* New Conversation Modal */}
      <Modal
        title="Start New Conversation"
        open={newConversationModalVisible}
        onCancel={() => {
          setNewConversationModalVisible(false);
          form.resetFields();
          setUserSearchOptions([]);
        }}
        onOk={() => form.submit()}
        okText="Create Conversation"
        cancelText="Cancel"
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleCreateConversation}
        >
          <Form.Item
            name="name"
            label="Conversation Name (Optional)"
          >
            <Input placeholder="Enter conversation name..." />
          </Form.Item>

          <Form.Item
            name="participants"
            label="Add Participants"
            rules={[{ required: true, message: 'Please select at least one participant' }]}
          >
            <Select
              mode="multiple"
              placeholder="Search and select users..."
              showSearch
              filterOption={false}
              onSearch={handleUserSearch}
              loading={isSearchingUsers}
              options={userSearchOptions}
              notFoundContent={isSearchingUsers ? <Spin size="small" /> : 'No users found'}
            />
          </Form.Item>

          <div style={{ fontSize: '12px', color: '#666', marginTop: '8px' }}>
            <p>This conversation will be associated with patient: <strong>
              {loadingPatientInfo ? (
                'Loading...'
              ) : (() => {
                const patientName = getPatientDisplayName(patientInfo);
                return patientName || patientId;
              })()}
            </strong></p>
            <p>You will be automatically added as a participant.</p>
          </div>
        </Form>
      </Modal>
    </Layout>
  );
};

export default Chatbox;
